import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, PanelRight, Minimize2, Maximize2, BookOpen, CheckCircle2, Clock, UserX } from "lucide-react";
import { JitsiMeeting } from "@jitsi/react-sdk";
import { logJitsiEvents } from "../../../utils/jitsiDebug";
import JitsiCallControls from "./JitsiCallControls";
import { leaveJitsi, hangupNow } from "../../../utils/jitsiLeave";
import { zegoCallConfig } from "../../../utils/zegoCallConfig";
import api from "../../../api";
import useFeatures from "../../../hooks/useFeatures";
import AssignToolModal from "../../../components/clinical/AssignToolModal";

// Opens an embedded video call for an Online appointment. The provider
// (self-hosted Jitsi / ZegoCloud / Google Meet) is decided server-side on
// the first join of this appointment and pinned there - this component
// just renders whichever one the join call returns. See
// app/meeting/meeting_service.py (backend) for the fallback-chain logic.
// onRecordingAllowedChange(bool): for an automatic recorder running on the
// doctor's laptop during a Jitsi call - there's no start/stop button, so it
// must follow this. Fires with the patient's current choice whenever it's
// (re)checked; true only for an explicit, current "granted" (fails closed).
// layout: "full" (modal over the page), "side" (docked on the right, page
// stays usable - default on the consultation pages so the doctor can write
// notes during the call) or "mini" (small corner window). Switching only
// changes CSS classes on the same elements, so the call isn't re-joined.
// onLayoutChange(layout) lets the page make room for the side panel.
const LAYOUT_CLASSES = {
  full: {
    outer: "fixed inset-0 z-50 flex items-center justify-center sm:p-4 bg-black/50 backdrop-blur-xs",
    inner: "bg-white w-full h-[100dvh] sm:h-[80vh] sm:max-w-4xl rounded-none sm:rounded-xl shadow-xl flex flex-col overflow-hidden",
  },
  side: {
    outer: "fixed z-50 inset-y-0 right-0 w-full sm:w-[420px] xl:w-[500px] sm:p-3 pointer-events-none",
    inner: "pointer-events-auto bg-white w-full h-full rounded-none sm:rounded-xl shadow-2xl border border-secondary-200 flex flex-col overflow-hidden",
  },
  mini: {
    outer: "fixed z-50 bottom-4 right-4 w-[300px] h-[230px] sm:w-[380px] sm:h-[270px] pointer-events-none",
    inner: "pointer-events-auto bg-white w-full h-full rounded-xl shadow-2xl border border-secondary-200 flex flex-col overflow-hidden",
  },
};

// Session room (E2): the clinician can give a toolkit item mid-call (it lands
// in the patient's My Care to do after the session); closing asks how the session ended, records attendance
// (feature "attendance"), and passes the outcome to onClose(outcome) so the
// page can open the note.
const OUTCOMES = [
  { key: "completed", label: "Session completed", icon: CheckCircle2, cls: "border-emerald-300 hover:bg-emerald-50 text-emerald-800" },
  { key: "ended_early", label: "Ended early", icon: Clock, cls: "border-amber-300 hover:bg-amber-50 text-amber-800" },
  { key: "no_show", label: "Patient didn't join", icon: UserX, cls: "border-rose-300 hover:bg-rose-50 text-rose-800" },
];

const VideoCallModal = ({ appointment, onClose: onCloseProp, onRecordingAllowedChange, defaultLayout = "full", onLayoutChange }) => {
  const features = useFeatures();
  const [askOutcome, setAskOutcome] = useState(false);
  const [sharingTool, setSharingTool] = useState(false);
  const [patientJoined, setPatientJoined] = useState(false);
  const askable = Boolean(appointment?.id && features.has("attendance"));
  // Hang up in Jitsi before the window goes, so the room frees the doctor's
  // seat straight away (see utils/jitsiLeave.js); otherwise a rejoin hits the
  // two-person limit and the patient still sees the doctor as present.
  const jitsiApiRef = useRef(null);
  const closingRef = useRef(false);
  const closeCall = async (outcome) => {
    if (closingRef.current) return;  // e.g. our timer and Zego's leave event at once
    closingRef.current = true;
    const jitsi = jitsiApiRef.current;
    jitsiApiRef.current = null;
    await leaveJitsi(jitsi);
    // Stops the session's audio recording (Zego calls) so it's uploaded.
    if (appointment?.id) api.callEnded(appointment.id).catch(() => {});
    onCloseProp(outcome);
  };
  useEffect(() => {
    const onPageHide = () => hangupNow(jitsiApiRef.current);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      const jitsi = jitsiApiRef.current;
      jitsiApiRef.current = null;
      if (jitsi) leaveJitsi(jitsi, 0);
    };
  }, []);
  const onClose = () => (askable ? setAskOutcome(true) : closeCall());
  // Pressing the call's own hang-up (Jitsi or Zego) closes this window too,
  // through the same outcome question as the X. The SDKs keep the callback
  // they were first given, so they call it through a ref.
  const [callEnded, setCallEnded] = useState(false);
  const [jitsiApi, setJitsiApi] = useState(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const endedByCall = () => { setCallEnded(true); jitsiApiRef.current = null; onCloseRef.current(); }; // already left

  // Session length (backend scheduling/session_limits.py): from 5 minutes before
  // the end, count down and offer Extend while extensions are left; at the
  // end, hang up and end the call for everyone. The server keeps the time and
  // the count, so a reload or rejoin can't reset it.
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [provider, setProvider] = useState(null);
  const zpRef = useRef(null);
  const [timer, setTimer] = useState(null);
  const [tick, setTick] = useState(Date.now());
  const [extending, setExtending] = useState(false);
  const autoEndedRef = useRef(false);
  const loadTimer = () => {
    if (!appointment?.id) return;
    api.getSessionTimer(appointment.id).then((r) => setTimer({ ...r.data, at: Date.now() })).catch(() => {});
  };
  useEffect(() => {
    if (status !== "ready") return undefined;
    loadTimer();
    const resync = setInterval(loadTimer, 60000);
    const clock = setInterval(() => setTick(Date.now()), 1000);
    return () => { clearInterval(resync); clearInterval(clock); };
  }, [status, appointment?.id]);
  const secondsLeft = timer && timer.seconds_left != null ? Math.round(timer.seconds_left - (tick - timer.at) / 1000) : null;
  const showCountdown = secondsLeft != null && secondsLeft <= (timer?.warn_before_seconds || 300) && !callEnded;
  // Google Meet runs in its own tab, so we can't end it: count down and allow
  // extensions as for the others, but ask the doctor to end it in Meet.
  const canAutoEnd = provider === "zego" || provider === "jitsi";
  const extendSession = async () => {
    setExtending(true);
    try {
      const r = await api.extendSession(appointment.id);
      setTimer({ ...r.data, at: Date.now() });
    } catch (e) {
      const data = e?.response?.data;
      if (data?.seconds_left != null) setTimer({ ...data, at: Date.now() });
      alert(data?.error || "Couldn't extend the session.");
    } finally {
      setExtending(false);
    }
  };
  useEffect(() => {
    if (secondsLeft == null || secondsLeft > 0 || autoEndedRef.current || callEnded || !canAutoEnd) return;
    autoEndedRef.current = true;  // time's up: end it for everyone
    try { zpRef.current?.hangUp(); } catch (e) { /* already left */ }
    const jitsi = jitsiApiRef.current;
    jitsiApiRef.current = null;
    if (jitsi) leaveJitsi(jitsi);
    if (appointment?.id) api.callEnded(appointment.id).catch(() => {});
    setCallEnded(true);
    if (askable) setAskOutcome(true);
    else closeCall();
  }, [secondsLeft]);
  const mmss = (s) => `${Math.floor(Math.max(0, s) / 60)}:${String(Math.max(0, s) % 60).padStart(2, "0")}`;
  const finish = async (outcome) => {
    if (outcome) {
      try {
        await api.setAppointmentAttendance(appointment.id, outcome === "no_show" ? "no_show" : "attended");
      } catch (e) {
        // e.g. the slot hasn't started yet - the outcome still goes to the page
      }
    }
    await closeCall(outcome || null);
  };
  const [layout, setLayoutState] = useState(LAYOUT_CLASSES[defaultLayout] ? defaultLayout : "full");
  const setLayout = (next) => { setLayoutState(next); if (onLayoutChange) onLayoutChange(next); };
  useEffect(() => {
    if (onLayoutChange) onLayoutChange(layout);
    return () => { if (onLayoutChange) onLayoutChange(null); };
  }, []);
  const [credentials, setCredentials] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  // Jitsi-only: this file is only ever used from the doctor side, so
  // "the other party" is always the patient. Both start false - the
  // overlay must stay hidden through Jitsi's own prejoin screen (camera/
  // mic preview, name entry, "Join meeting" button) and only turn on once
  // *you* have actually entered the room; onApiReady fires as soon as the
  // iframe itself loads, well before that, so it can't drive this alone.
  const [hasJoinedCall, setHasJoinedCall] = useState(false);
  const [waitingForPatient, setWaitingForPatient] = useState(false);
  // The patient's recording choice (asked in the appointment app before a
  // Jitsi/Zego call). Polled while the call is open, since the patient can
  // change their mind at any time during it.
  const [recordingConsent, setRecordingConsent] = useState(null);
  const [recordingAllowed, setRecordingAllowed] = useState(false);
  // What Zego is actually doing: "recording" | "paused" | "off".
  const [recordingState, setRecordingState] = useState("off");
  // A pending "please turn transcription on" sent to the patient's screen.
  const [consentRequest, setConsentRequest] = useState(null);
  const [askingPatient, setAskingPatient] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.getRecordingConsent(appointment.id);
        if (cancelled) return;
        setRecordingConsent(res.data?.consent || null);
        setRecordingAllowed(Boolean(res.data?.recording_allowed));
        setRecordingState(res.data?.recording_state || "off");
        setConsentRequest(res.data?.consent_request || null);
      } catch {
        // Couldn't check - treat as not allowed until the next check.
        if (!cancelled) setRecordingAllowed(false);
      }
    };
    load();
    // The patient can change their mind mid-call, so keep re-checking.
    const timer = setInterval(load, 20000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [appointment.id]);

  useEffect(() => {
    if (typeof onRecordingAllowedChange === "function") onRecordingAllowedChange(recordingAllowed);
  }, [recordingAllowed, onRecordingAllowedChange]);

  const [togglingConsent, setTogglingConsent] = useState(false);
  // Ask the patient to turn transcription on: their app shows a prompt and
  // only their own tap records consent (the strongest evidence under DPDP).
  const askPatientToTurnOn = async () => {
    setAskingPatient(true);
    try {
      const res = await api.requestRecordingConsent(appointment.id);
      setConsentRequest(res.data?.consent_request || { requested_at: new Date().toISOString() });
    } catch (e) {
      alert(e?.response?.data?.error || "Couldn't send the request to the patient.");
    } finally {
      setAskingPatient(false);
    }
  };

  const handleToggleConsent = async () => {
    setTogglingConsent(true);
    const nextDecision = recordingAllowed ? "declined" : "granted";
    try {
      const res = await api.updateRecordingConsent(appointment.id, nextDecision);
      setRecordingConsent(res.data?.consent || null);
      setRecordingAllowed(Boolean(res.data?.recording_allowed));
      // The recording pauses/resumes in the background; show the result.
      setTimeout(() => {
        api.getRecordingConsent(appointment.id).then((r) => setRecordingState(r.data?.recording_state || "off")).catch(() => {});
      }, 4000);
    } catch (e) {
      alert("Could not update transcription consent: " + (e?.response?.data?.error || e.message));
    } finally {
      setTogglingConsent(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.joinAppointmentMeeting(appointment.id);
        if (cancelled) return;
        setProvider(res.data.provider);
        setCredentials(res.data.credentials);
        setStatus("ready");
      } catch (e) {
        if (cancelled) return;
        setErrorMessage(e?.response?.data?.error || "No video provider is available right now.");
        setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appointment.id]);

  useEffect(() => {
    if (provider !== "zego" || !credentials) return;
    let zp;
    let guards;
    import("@zegocloud/zego-uikit-prebuilt").then(({ ZegoUIKitPrebuilt }) => {
      const container = document.getElementById("zego-call-container");
      if (!container) return;
      const kitToken = ZegoUIKitPrebuilt.generateKitTokenForProduction(
        credentials.app_id,
        credentials.token,
        credentials.room_name,
        credentials.user_id,
        credentials.user_name
      );
      zp = ZegoUIKitPrebuilt.create(kitToken);
      zpRef.current = zp;
      // Camera off until turned on, 360p (never above 720p), no screen
      // share, and guards against forgotten calls (utils/zegoCallConfig.js).
      guards = zegoCallConfig({
        ZegoUIKitPrebuilt, getZp: () => zp, audioOnly: !!appointment?.audio_only,
        durationMinutes: appointment?.duration_minutes, onLeaveRoom: endedByCall,
        onOthersChange: (n) => { setWaitingForPatient(n === 0); if (n > 0) setPatientJoined(true); setHasJoinedCall(true); },
      });
      zp.joinRoom({ container, ...guards.config });
    });
    return () => {
      guards?.dispose();
      try {
        zp?.destroy();
      } catch (e) {
        // best-effort cleanup only
      }
    };
  }, [provider, credentials]);

  // Portalled to <body>: rendered inside DashboardLayout's main area (its own
  // stacking context, z-0), even z-50 sat under the layout's fixed
  // notification bells (z-40), which then covered the call's header buttons.
  return createPortal(
    <div
      // No click-outside-to-close: a stray click on the backdrop used to end
      // the call. Only the X button closes it. Full screen on phones.
      className={LAYOUT_CLASSES[layout].outer}
    >
      <div className={LAYOUT_CLASSES[layout].inner}>
        <div className={`flex items-center justify-between border-b border-secondary-100 ${layout === "mini" ? "px-3 py-1.5" : "px-5 py-3"}`}>
          <h3 className={`font-semibold text-secondary-800 ${layout === "mini" ? "text-sm" : ""}`}>Video Call</h3>
          {provider !== "google_meet" && layout !== "mini" && (
            <div className="flex items-center gap-1.5 mx-3">
              <span
                className={`truncate text-xs font-semibold px-2 py-1 rounded-md ${
                  recordingAllowed
                    ? "bg-green-50 text-green-700"
                    : recordingConsent?.decision === "declined"
                      ? "bg-amber-50 text-amber-700"
                      : "bg-secondary-100 text-secondary-600"
                }`}
                title="The patient's choice about transcribing this session"
              >
                {recordingAllowed
                  ? "Transcription: on"
                  : recordingConsent?.source === "doctor_on_behalf_of_patient"
                    ? "Transcription: paused (this session)"
                    : recordingConsent?.decision === "declined"
                      ? "Transcription: off (declined)"
                      : "Transcription: off (pending)"}
                {provider === "zego" && recordingState === "recording" && " · recording"}
                {provider === "zego" && recordingState === "paused" && " · recording paused"}
              </span>
              <button
                type="button"
                disabled={togglingConsent}
                onClick={handleToggleConsent}
                className={`text-xs px-2 py-0.5 rounded border font-medium transition disabled:opacity-50 ${
                  recordingAllowed
                    ? "border-amber-300 text-amber-800 hover:bg-amber-50"
                    : "border-green-300 text-green-700 hover:bg-green-50"
                }`}
                title={recordingAllowed ? "Pause transcription for this session (patient's future preference remains saved)" : "Resume transcription for this session"}
              >
                {togglingConsent ? "…" : recordingAllowed ? "Pause" : "Resume"}
              </button>
              {!recordingAllowed && (
                consentRequest ? (
                  <span className="text-xs text-secondary-500" title="The patient sees a prompt in their call and decides themselves">
                    Asked patient · waiting
                  </span>
                ) : (
                  <button
                    type="button"
                    disabled={askingPatient}
                    onClick={askPatientToTurnOn}
                    className="text-xs px-2 py-0.5 rounded border border-primary-300 text-primary-700 font-medium hover:bg-primary-50 disabled:opacity-50"
                    title="Show the patient a prompt to turn transcription on themselves. Their own tap is recorded as their consent."
                  >
                    {askingPatient ? "…" : "Ask patient"}
                  </button>
                )
              )}
            </div>
          )}
          <div className="flex items-center gap-0.5 shrink-0">
            {[
              { key: "mini", icon: Minimize2, label: "Small window" },
              { key: "side", icon: PanelRight, label: "Side panel - keep writing notes" },
              { key: "full", icon: Maximize2, label: "Full screen" },
            ].filter((o) => o.key !== layout).map((o) => (
              <button key={o.key} type="button" onClick={() => setLayout(o.key)} title={o.label} aria-label={o.label}
                className="p-1.5 hover:bg-secondary-100 rounded-full text-secondary-600">
                <o.icon size={16} />
              </button>
            ))}
            {features.has("toolkit") && appointment?.patient_id && (
              <button type="button" onClick={() => setSharingTool(true)} title="Send a worksheet or exercise to the patient's app - they do it after the call" aria-label="Assign homework"
                className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold text-primary-700 hover:bg-primary-50">
                <BookOpen size={15} /><span className="hidden sm:inline">Assign homework</span>
              </button>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-secondary-100 rounded-full" title="End and close" aria-label="Close video call">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex-1 relative min-h-0 overflow-hidden">
          {status === "loading" && (
            <div className="h-full flex items-center justify-center text-secondary-500">Connecting...</div>
          )}
          {status === "error" && (
            <div className="h-full flex items-center justify-center text-red-600 text-sm px-6 text-center">
              {errorMessage}
            </div>
          )}
          {status === "ready" && provider === "jitsi" && (
            <>
              <JitsiMeeting
                domain={credentials.domain}
                roomName={credentials.room_name}
                jwt={credentials.jwt}
                onReadyToClose={endedByCall}
                configOverwrite={{
                  // Joins happen inside this embedded window, so never show
                  // Jitsi's mobile "Join in app / App Store" page - patients on
                  // phones would otherwise land there instead of the call.
                  // Both keys: older Jitsi builds read disableDeepLinking,
                  // newer ones deeplinking.disabled.
                  // Every call goes through the video bridge (JVB), never peer-to-peer.
                  // On the live server the switch from the bridge to a direct connection
                  // often fails half-way; lib-jitsi-meet then throws on the half-built P2P
                  // session ("reading 'setSenderVideoConstraints'") and the camera stops
                  // reaching the other side. Both sides must have this.
                  p2p: { enabled: false },
                  disableDeepLinking: true,
                  deeplinking: { disabled: true },
                  // Jitsi's own toolbar is hidden: the doctor gets only our mic,
                  // camera and device-settings bar (JitsiCallControls); the header's
                  // X ends the call. That also keeps screen sharing out: a
                  // clinician's screen holds other patients' details and private
                  // notes. Tools go to the patient's phone instead ("Share a tool").
                  // Voice-call visit: start with the camera off and no camera button.
                  toolbarButtons: [],
                  ...(appointment?.audio_only ? { startWithVideoMuted: true, startAudioOnly: true } : {}),
                }}
                getIFrameRef={(el) => {
                  el.style.height = "100%";
                  el.style.width = "100%";
                }}
                onApiReady={(externalApi) => {
                  logJitsiEvents(externalApi);
                  setJitsiApi(externalApi);
                  jitsiApiRef.current = externalApi;
                  const updateWaiting = () => {
                    setWaitingForPatient(externalApi.getNumberOfParticipants() < 2);
                  };
                  externalApi.on("videoConferenceJoined", () => {
                    setHasJoinedCall(true);
                    updateWaiting();
                  });
                  externalApi.on("videoConferenceLeft", () => setHasJoinedCall(false));
                  externalApi.on("participantJoined", () => { setPatientJoined(true); updateWaiting(); });
                  externalApi.on("participantLeft", updateWaiting);
                }}
              />
              {hasJoinedCall && waitingForPatient && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/70 pointer-events-none">
                  <p className="text-white text-sm font-medium px-6 py-3 bg-black/40 rounded-lg">
                    Waiting for the patient to join the meeting...
                  </p>
                </div>
              )}
              {hasJoinedCall && <JitsiCallControls api={jitsiApi} audioOnly={!!appointment?.audio_only} />}
            </>
          )}
          {showCountdown && (
            <div role="status" className="absolute top-3 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 whitespace-nowrap rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-white shadow-xl">
              <Clock size={16} />
              <span>{secondsLeft > 0 ? `Session ends in ${mmss(secondsLeft)}` : "Session time is up"}</span>
              {timer.extensions_left > 0 && secondsLeft > 0 ? (
                <button type="button" disabled={extending} onClick={extendSession}
                  className="rounded-full bg-white px-3 py-1 text-xs font-bold text-amber-700 hover:bg-amber-50 disabled:opacity-60">
                  {extending ? "…" : `Extend ${timer.extension_minutes} min (${timer.extensions_left} left)`}
                </button>
              ) : (
                <span className="text-xs font-normal text-white/90">
                  {canAutoEnd ? "No extensions left · the call ends automatically" : "Please end the call in Google Meet"}
                </span>
              )}
            </div>
          )}
          {status === "ready" && provider === "zego" && hasJoinedCall && waitingForPatient && (
            <div className="absolute inset-x-0 top-4 z-30 flex justify-center pointer-events-none">
              <p className="text-white text-sm font-medium px-5 py-2.5 bg-black/60 rounded-lg">
                Waiting for the patient to join the meeting...
              </p>
            </div>
          )}
          {status === "ready" && provider === "zego" && (
            // Pinned to the call area: Zego otherwise grows taller than the window
            // and its control bar (mic, camera, leave) ends up below the edge.
            <div id="zego-call-container" className="absolute inset-0 overflow-hidden" />
          )}
          {status === "ready" && provider === "google_meet" && (
            <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="text-sm text-secondary-600">
                This call can't be embedded in-app - it's using the Google Meet fallback.
              </p>
              <a
                href={credentials.meeting_link}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                Open Google Meet in new tab
              </a>
            </div>
          )}
          {askOutcome && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 p-4">
              <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl space-y-3">
                <p className="text-base font-bold text-secondary-900">How did the session end?</p>
                <p className="text-xs text-secondary-600">This records attendance. The call closes after you choose.</p>
                <div className="space-y-2">
                  {OUTCOMES.filter((o) => o.key !== "no_show" || !patientJoined).map((o) => (
                    <button key={o.key} type="button" onClick={() => finish(o.key)}
                      className={`flex w-full items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold ${o.cls}`}>
                      <o.icon size={16} /> {o.label}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between pt-1">
                  {callEnded ? <span /> : <button type="button" onClick={() => setAskOutcome(false)} className="text-xs text-secondary-600 underline">Back to the call</button>}
                  <button type="button" onClick={() => finish(null)} className="text-xs text-secondary-600 underline">Close without recording</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {sharingTool && (
        <AssignToolModal patientId={appointment.patient_id} patientName={appointment.patient_name} appointmentId={appointment.id}
          onClose={() => setSharingTool(false)} />
      )}
    </div>,
    document.body
  );
};

export default VideoCallModal;
